import cx from 'classnames'
import { FC, useMemo } from 'react'
import { motionGroup, motionSlide, slideReveal } from 'src/hooks/useAppear'
import { colors } from 'src/theme'
import { MotionMask, MotionWrapper } from 'src/theme/styled'

import { ActionButtonProps } from '../ActionButton'
import { BlockTitleProps } from '../BlockTitle'
import { PictureProps } from '../Picture'
import { RichTextProps } from '../RichText'

import * as SC from './styled'

export type DuoPushProps = {
  className?: string
  blockTitleProps?: BlockTitleProps
  pushes?: (MinimalSeoBlock & {
    title?: string
    richtextProps?: RichTextProps
    actionButtonProps?: ActionButtonProps
    pictureProps?: PictureProps
  })[]
  color?: string
}

const DuoPush: FC<DuoPushProps> = ({
  className,
  blockTitleProps,
  pushes,
  color,
}) => {
  const { motions, variants } = useDuoPushMotion()

  return (
    <SC.Container className={cx('DuoPush', className)} $color={color}>
      {blockTitleProps && (
        <SC._BlockTitle
          {...blockTitleProps}
          color={color ? colors.black : colors.yellowPale}
        />
      )}
      {pushes && pushes.length > 0 && (
        <SC.Pushes
          slides={pushes.map((push, index) => {
            return {
              node: (
                <SC.Push key={index} color={color} as={push.htmlTag}>
                  {push.pictureProps && (
                    <SC._Picture
                      {...push.pictureProps}
                      {...(index % 2
                        ? motions.pictureDelayed
                        : motions.picture)}
                    />
                  )}
                  <SC.PushCaption
                    {...(index % 2 ? motions.captionDelayed : motions.caption)}
                  >
                    {push.title && (
                      <MotionMask>
                        <SC.Title
                          as={push.htmlTitleTag}
                          variants={variants.text}
                        >
                          {push.title}
                        </SC.Title>
                      </MotionMask>
                    )}
                    {push.richtextProps && (
                      <MotionMask>
                        <MotionWrapper variants={variants.text}>
                          <SC._RichText {...push.richtextProps} />
                        </MotionWrapper>
                      </MotionMask>
                    )}
                    {push.actionButtonProps && (
                      <SC._ActionButton {...push.actionButtonProps} />
                    )}
                  </SC.PushCaption>
                </SC.Push>
              ),
            }
          })}
          keenOptions={{
            slides: { perView: 1.2, spacing: 16 },
            defaultAnimation: {
              duration: 300,
            },
            breakpoints: {
              // 600 as in sizes
              '(min-width: 37.5em)': {
                slides: { perView: 1.3, spacing: 24 },
              },
              // 750 as in sizes
              '(min-width: 56.25em)': {
                disabled: true,
              },
            },
          }}
        />
      )}
    </SC.Container>
  )
}

const useDuoPushMotion = () => {
  const motions = useMemo(
    () => ({
      picture: motionSlide({}).motionProps,
      pictureDelayed: motionSlide({ delay: 0.12 }).motionProps,
      caption: motionGroup({ staggerChildren: 0.12 }).motionProps,
      captionDelayed: motionGroup({ staggerChildren: 0.12, delay: 0.12 })
        .motionProps,
    }),
    []
  )

  const variants = useMemo(
    () => ({
      text: slideReveal({ y: '100%' }),
    }),
    []
  )

  return { motions, variants }
}

export default DuoPush
